import { createContext, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { requestGeneration } from "../../abstract/api/rest";
import { Pages } from "../../abstract/controller/enum/Pages";
import { exportToYamlFunction } from "../../abstract/controller/modelYamlExportFunction";
import { getNodeById } from "../../abstract/controller/nodeSelector";
import * as nodeUtils from "../../abstract/controller/utils/nodeUtils";
import * as pathUtils from "../../abstract/controller/utils/pathUtils";
import * as systemUtils from "../../abstract/controller/utils/systemUtils";
import {
  CFG_ALGORITHM,
  CFG_ASSIGNMENTS,
  CFG_CHOICE_NAMES,
  CFG_CONSTRAINTS,
  CFG_FILTERS,
  CFG_FILTERS_TARGET,
  CFG_FILTERS_TYPE,
  CFG_FILTERS_VALUE,
  CFG_HEADER,
  CFG_NWISE_COVERAGE,
  CFG_NWISE_N,
  CFG_NWISE_TUPLES,
  CFG_RND_ADAPTIVE,
  CFG_RND_DUPLICATES,
  CFG_RND_LENGTH,
  CFG_SEPARATOR,
  CFG_TUPLES,
  VAL_ALG_CARTESIAN,
  VAL_ALG_NWISE,
  VAL_ALG_RANDOM,
  VAL_ASSIGNMENTS_IGNORE,
  VAL_ASSIGNMENTS_NORMAL,
  VAL_CONSTRAINTS_IGNORE,
  VAL_CONSTRAINTS_INVERT,
  VAL_CONSTRAINTS_NEGATE,
  VAL_CONSTRAINTS_NORMAL,
  VAL_FALSE,
  VAL_FILTER_ALL,
  VAL_FILTER_ASSIGNMENT,
  VAL_FILTER_BLACKLIST,
  VAL_FILTER_CHILDREN,
  VAL_FILTER_CHOICE,
  VAL_FILTER_CONSTRAINT,
  VAL_FILTER_LOGIC,
  VAL_FILTER_PARAMETER,
  VAL_FILTER_WHITELIST,
  VAL_TRUE,
} from "../../abstract/names/GeneratorNames";
import {
  addData,
  addErrors,
  addGeneratorFilter,
  getGeneratorData,
  modifyGeneratorData,
  removeGeneratorFilter,
  resetData,
  setProgress,
  setShowPageGenerator,
} from "../../abstract/redux/slice/generatorSlice";
import { setSpinner, setSpinnerMessage } from "../../abstract/redux/slice/overlaySlice";
import { getIdFunction, getRoot } from "../../abstract/redux/slice/structureSlice";
import { ButtonDefault } from "../../components/ui/buttons/ButtonDefault";
import DefaultTooltip, * as vendorTooltip from "../../components/ui/tooltips/DefaultTooltip";
import IconInfoCircle from "../../themes/icons/IconInfoCircle";
import IconTrashOutline from "../../themes/icons/IconTrashOutline";
import AutoComplete from "./AutoComplete";
import styles from "./DataGenerationPage.module.scss";
import { getEndpointTomato } from "../../abstract/redux/slice/endpointSlice";
import { ButtonGenerate } from "../../components/ui/buttons/ButtonGenerate";

const GenerationContext = createContext();

const DataGenerationPage = () => {
  const _navigate = useNavigate();
  const _dispatch = useDispatch();
  const _root = useSelector(getRoot);
  const _idFunction = useSelector(getIdFunction);
  const _formData = useSelector(getGeneratorData);

  const _nodeFunction = getNodeById(_root, _idFunction)?.data;
  const _nameFunction = nodeUtils.getName(_nodeFunction);

  const _isDev = systemUtils.isEnvDev();

  useEffect(() => {
    if (_idFunction === "") {
      _navigate(Pages.ROOT);
    }
  }, [_navigate, _idFunction]);

  const _getParameters = (node) => {
    const parameters = [];

    nodeUtils.visitAllParameters(node, (e) => parameters.push(e));

    return parameters;
  };

  const _getParametersNames = (params) => {
    return params.map((e) => pathUtils.cnvFunFrontToBackPath(nodeUtils.getID(e)));
  };

  const _getChoices = (parameters) => {
    const choices = [];

    parameters.forEach((e) => {
      nodeUtils.getChoices(e).forEach((f) => {
        choices.push(f);
      });
    });

    return choices;
  };

  const _getChoicesNames = (choices) => {
    const names = new Set();

    choices.forEach((e) => {
      names.add(nodeUtils.getName(e));
    });

    return Array.from(names);
  };

  const _getNamesAndValues = (parameters, choices) => {
    const elements = new Set();

    parameters.forEach((e) => {
      elements.add(nodeUtils.getName(e) ?? "");
    });

    choices.forEach((e) => {
      elements.add(nodeUtils.getName(e) ?? "");
      elements.add(nodeUtils.getValue(e) ?? "");
    });

    return Array.from(elements);
  };

  const _getLabels = (parameters) => {
    const labels = new Set();

    parameters.forEach((e) => {
      const value = nodeUtils.getLabel(e);

      value.forEach((label) => {
        labels.add(label);
      });
    });

    return Array.from(labels);
  };

  const _param = _getParameters(_nodeFunction);
  const _paramNames = _getParametersNames(_param);
  const _paramLabels = _getLabels(_param);
  const _choice = _getChoices(_param);
  const _choiceNames = _getChoicesNames(_choice);
  const _constraints = nodeUtils.getConditionsConstraints(_nodeFunction);
  const _constraintsNames = _constraints.map((e) => nodeUtils.getName(e));
  const _assignments = nodeUtils.getConditionsAssignments(_nodeFunction);
  const _assignmentsNames = _assignments.map((e) => nodeUtils.getName(e));

  const _namesAndValues = _getNamesAndValues(_param, _choice);

  const _suggestions = {
    nwise: [..._paramNames, ..._paramLabels],
    all: [..._paramNames, ..._choiceNames, ..._constraintsNames, ..._assignmentsNames],
    parameters: _paramNames,
    choices: _choiceNames,
    parametersAndChoices: [..._paramNames, ..._choiceNames],
    constraints: _constraintsNames,
    assignments: _assignmentsNames,
    constraintsAndAssignments: [..._constraintsNames, ..._assignmentsNames],
  };

  const _handleModifyForm = (e, index = -1) => {
    const { name, value } = e.target;

    _dispatch(modifyGeneratorData({ name, value, index }));
  };

  const _getDelimiter = () => {
    const _suggestions = [",", ";", "|"];

    main: for (let i = 0; i < _suggestions.length; i++) {
      for (let j = 0; j < _namesAndValues.length; j++) {
        if (_namesAndValues[j].includes(_suggestions[i])) {
          continue main;
        }
      }
      return _suggestions[i];
    }

    return "🍅";
  };

  const _modelYaml = exportToYamlFunction(_root, _idFunction);
  const _delimiter = _getDelimiter(_modelYaml);

  useEffect(() => {
    _dispatch(modifyGeneratorData({ name: CFG_SEPARATOR, value: _delimiter, index: -1 }));
  }, [_delimiter, _dispatch]);

  return (
    <GenerationContext.Provider
      value={{
        root: _root,
        id: _idFunction,
        node: _nodeFunction,
        name: _nameFunction,
        data: _formData,
        suggestions: _suggestions,
        modelYaml: _modelYaml,
        isDev: _isDev,
        handleChange: _handleModifyForm,
      }}>
      <Gen>
        <GenHeader>
          <GenHeaderNavigation />
          <GenHeaderTitle />
        </GenHeader>
        <GenBody>
          <GenBodyTitle />
          <GenBodyAlg />
          <GenBodyAlgFilter />
          <GenBodyAlgLogic />
        </GenBody>
        <GenFooter>
          <GenFooterButtons />
        </GenFooter>
      </Gen>
    </GenerationContext.Provider>
  );
};

const Gen = ({ children }) => {
  return <div className={styles["page"]}>{children}</div>;
};

const GenHeader = ({ children }) => {
  return <div className={styles["header"]}>{children}</div>;
};

const GenHeaderNavigation = ({ back }) => {
  const _dispatch = useDispatch();

  const _handleBackClick = () => {
    _dispatch(setShowPageGenerator(false));
  };

  return (
    <div className={styles["header__options"]}>
      <div className={styles["header__options--button"]}>
        <ButtonDefault text={"Back to model editor"} handler={_handleBackClick} wide />
      </div>
      <ButtonDefault text={"X"} handler={_handleBackClick} auto />
    </div>
  );
};

const GenHeaderTitle = () => {
  const { name } = useContext(GenerationContext);

  return (
    <div className={styles["header__name"]}>
      Generate tests for <span className={styles["header__name-title"]}>{name}</span>
    </div>
  );
};

const GenBody = ({ children }) => {
  return <div className={styles["body"]}>{children}</div>;
};

const GenBodyTitle = () => {
  return <h1>Generation data</h1>;
};

const GenBodyAlg = () => {
  const { data, suggestions, handleChange } = useContext(GenerationContext);

  return (
    <>
      <div className={styles["body__algorithm"]}>
        <h2>Algorithm</h2>
        <select
          className={styles["input-default"]}
          name={CFG_ALGORITHM}
          value={data[CFG_ALGORITHM]}
          onChange={(e) => handleChange(e)}>
          <option value={VAL_ALG_NWISE}>{VAL_ALG_NWISE}</option>
          <option value={VAL_ALG_RANDOM}>{VAL_ALG_RANDOM}</option>
          <option value={VAL_ALG_CARTESIAN}>{VAL_ALG_CARTESIAN}</option>
        </select>
      </div>
      <GenBodyAlgNWise data={data} suggestions={suggestions} handleChange={handleChange} />
      <GenBodyAlgRandom data={data} handleChange={handleChange} />
    </>
  );
};

const GenBodyAlgNWise = ({ data, suggestions, handleChange }) => {
  if (data[CFG_ALGORITHM] !== VAL_ALG_NWISE) {
    return null;
  }

  return (
    <div className={styles["body__algorithm"]}>
      <h2>n-wise parameters</h2>
      <div className={styles["body__row"]}>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-n-wise-n`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-n-wise-n`}>
              Specify the value of n for n-wise testing. Must be lower than number of parameters.
            </DefaultTooltip>
            <div className={styles["body__text"]}>N</div>
          </div>
          <input
            required
            className={styles["input-default"]}
            type="number"
            name={CFG_NWISE_N}
            value={data[CFG_NWISE_N]}
            onChange={(e) => handleChange(e, -1)}
            min="1"
          />
        </div>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-n-wise-coverage`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-n-wise-coverage`}>
              Specify the coverage percentage of combinations in the test cases.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Coverage</div>
          </div>
          <input
            required
            className={styles["input-default"]}
            type="number"
            name={CFG_NWISE_COVERAGE}
            value={data[CFG_NWISE_COVERAGE]}
            onChange={(e) => handleChange(e, -1)}
            min="1"
            max="100"
          />
        </div>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-n-wise-coverage`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-n-wise-coverage`}>
              Parameters that will be considered to calculate the coverage during the generation.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Tuples</div>
          </div>
          <AutoComplete
            name={CFG_TUPLES}
            suggestions={suggestions.nwise}
            onChange={(e) => handleChange(e, -1)}
            value={data[CFG_NWISE_TUPLES]}
          />
        </div>
      </div>
    </div>
  );
};

const GenBodyAlgRandom = ({ data, handleChange }) => {
  if (data[CFG_ALGORITHM] !== VAL_ALG_RANDOM) {
    return null;
  }

  return (
    <div className={styles["body__algorithm"]}>
      <h2>random parameters</h2>
      <div className={styles["body__row"]}>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-random-length`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-random-length`}>
              Specify the length for random generation.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Length</div>
          </div>
          <input
            required
            className={styles["input-default"]}
            type="number"
            name={CFG_RND_LENGTH}
            value={data[CFG_RND_LENGTH]}
            onChange={(e) => handleChange(e, -1)}
            min="0"
            max="100"
          />
        </div>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-random-duplicates`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-random-duplicates`}>
              Allow or disallow duplicates.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Duplicates</div>
          </div>
          <select
            className={styles["input-default"]}
            name={CFG_RND_DUPLICATES}
            value={data[CFG_RND_DUPLICATES]}
            onChange={(e) => handleChange(e, -1)}>
            <option value={VAL_FALSE}>{VAL_FALSE}</option>
            <option value={VAL_TRUE}>{VAL_TRUE}</option>
          </select>
        </div>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-random-adaptive`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-random-adaptive`}>
              Enable or disable adaptive generation.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Adaptive</div>
          </div>
          <select
            className={styles["input-default"]}
            name={CFG_RND_ADAPTIVE}
            value={data[CFG_RND_ADAPTIVE]}
            onChange={(e) => handleChange(e, -1)}>
            <option value={VAL_FALSE}>{VAL_FALSE}</option>
            <option value={VAL_TRUE}>{VAL_TRUE}</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const GenBodyAlgFilter = () => {
  const _dispatch = useDispatch();
  const { data, handleChange, suggestions } = useContext(GenerationContext);

  const _handleAddFilter = () => {
    _dispatch(addGeneratorFilter());
  };

  const _handleRemoveFilter = (index) => {
    _dispatch(removeGeneratorFilter(index));
  };

  const _getSuggestions = (index) => {
    switch (data?.[CFG_FILTERS][index][CFG_FILTERS_TARGET]) {
      case VAL_FILTER_ALL:
        return suggestions.all;
      case VAL_FILTER_PARAMETER:
        return suggestions.parameters;
      case VAL_FILTER_CHOICE:
        return suggestions.choices;
      case VAL_FILTER_CHILDREN:
        return suggestions.parametersAndChoices;
      case VAL_FILTER_CONSTRAINT:
        return suggestions.constraints;
      case VAL_FILTER_ASSIGNMENT:
        return suggestions.assignments;
      case VAL_FILTER_LOGIC:
        return suggestions.constraintsAndAssignments;
      default:
        return [];
    }
  };

  return (
    <div className={styles["body__algorithm"]}>
      <h2>Filters</h2>
      <div className={styles["body__row"]}>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-filter-type`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-filter-type`}>Select the filter type.</DefaultTooltip>
            <div className={styles["body__text"]}>Filter type</div>
          </div>
          {data[CFG_FILTERS].map((filter, index) => (
            <select
              className={styles["input-default"]}
              key={index}
              name={CFG_FILTERS_TYPE}
              value={filter[CFG_FILTERS_TYPE]}
              onChange={(e) => handleChange(e, index)}>
              <option value="">Select an option</option>
              <option value={VAL_FILTER_BLACKLIST}>{VAL_FILTER_BLACKLIST}</option>
              <option value={VAL_FILTER_WHITELIST}>{VAL_FILTER_WHITELIST}</option>
            </select>
          ))}
        </div>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-filtered-type`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-filtered-type`}>
              Select the type of filtered items.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Filtered type</div>
          </div>
          {data[CFG_FILTERS].map((filter, index) => (
            <select
              className={styles["input-default"]}
              key={index}
              name={CFG_FILTERS_TARGET}
              value={filter[CFG_FILTERS_TARGET]}
              onChange={(e) => handleChange(e, index)}>
              <option value="">Select an option</option>
              <option value={VAL_FILTER_ALL}>{VAL_FILTER_ALL}</option>
              <option value={VAL_FILTER_PARAMETER}>{VAL_FILTER_PARAMETER}</option>
              <option value={VAL_FILTER_CHOICE}>{VAL_FILTER_CHOICE}</option>
              <option value={VAL_FILTER_CHILDREN}>{VAL_FILTER_CHILDREN}</option>
              <option value={VAL_FILTER_CONSTRAINT}>{VAL_FILTER_CONSTRAINT}</option>
              <option value={VAL_FILTER_ASSIGNMENT}>{VAL_FILTER_ASSIGNMENT}</option>
              <option value={VAL_FILTER_LOGIC}>{VAL_FILTER_LOGIC}</option>
            </select>
          ))}
        </div>
        <div className={styles["body__col"]}>
          <div className={styles["body__row"]}>
            <div
              className={styles["body__icon"]}
              data-tooltip-id={`gen-filtered-labels`}
              data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
              <IconInfoCircle />
            </div>
            <DefaultTooltip handle={`gen-filtered-labels`}>
              Specify the labels and names to filter.
            </DefaultTooltip>
            <div className={styles["body__text"]}>Filtered labels and names</div>
          </div>
          {data[CFG_FILTERS].map((filter, index) => (
            <div className={styles["body__row-trash"]} key={index}>
              <AutoComplete
                name={CFG_TUPLES}
                suggestions={_getSuggestions(index)}
                onChange={(e) => handleChange(e, index)}
                value={filter[CFG_FILTERS_VALUE]}
              />
              <div className={styles["body__icon"]} onClick={() => _handleRemoveFilter(index)}>
                <IconTrashOutline />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles["body__button"]}>
        <ButtonDefault text={"Add filter"} handler={_handleAddFilter} wide />
      </div>
    </div>
  );
};

const GenBodyAlgLogic = () => {
  const _dispatch = useDispatch();
  const { data } = useContext(GenerationContext);

  const _handleRadioChange = (e) => {
    const { name, value } = e.target;

    _dispatch(modifyGeneratorData({ name, value, index: -1 }));
  };

  return (
    <div className={styles["body__algorithm"]}>
      <h2>Options</h2>
      <div className={styles["body__row"]}>
        <GenBodyConstraint
          constraint={data[CFG_CONSTRAINTS]}
          handleRadioChange={_handleRadioChange}
        />
        <GenBodyAssignment
          assignment={data[CFG_ASSIGNMENTS]}
          handleRadioChange={_handleRadioChange}
        />
        <GenBodyOptions assignment={data[CFG_ASSIGNMENTS]} handleRadioChange={_handleRadioChange} />
      </div>
    </div>
  );
};

const GenBodyConstraint = ({ constraint, handleRadioChange }) => {
  return (
    <div className={styles["body__col-logic"]}>
      <div className={styles["body__row-logic"]}>
        <div
          className={styles["body__icon"]}
          data-tooltip-id={`gen-logic-constraint`}
          data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
          <IconInfoCircle />
        </div>
        <DefaultTooltip handle={`gen-logic-constraint`}>
          Select how to process constraints.
        </DefaultTooltip>
        <div className={styles["body__text"]}>Constraints</div>
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="radio"
          name={CFG_CONSTRAINTS}
          value={VAL_CONSTRAINTS_NORMAL}
          checked={constraint === VAL_CONSTRAINTS_NORMAL}
          onChange={handleRadioChange}
        />
        {VAL_CONSTRAINTS_NORMAL}
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="radio"
          name={CFG_CONSTRAINTS}
          value={VAL_CONSTRAINTS_IGNORE}
          checked={constraint === VAL_CONSTRAINTS_IGNORE}
          onChange={handleRadioChange}
        />
        {VAL_CONSTRAINTS_IGNORE}
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="radio"
          name={CFG_CONSTRAINTS}
          value={VAL_CONSTRAINTS_NEGATE}
          checked={constraint === VAL_CONSTRAINTS_NEGATE}
          onChange={handleRadioChange}
        />
        {VAL_CONSTRAINTS_NEGATE}
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="radio"
          name={CFG_CONSTRAINTS}
          value={VAL_CONSTRAINTS_INVERT}
          checked={constraint === VAL_CONSTRAINTS_INVERT}
          onChange={handleRadioChange}
        />
        {VAL_CONSTRAINTS_INVERT}
      </div>
    </div>
  );
};

const GenBodyAssignment = ({ assignment, handleRadioChange }) => {
  return (
    <div className={styles["body__col-logic"]}>
      <div className={styles["body__row-logic"]}>
        <div
          className={styles["body__icon"]}
          data-tooltip-id={`gen-logic-assignment`}
          data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
          <IconInfoCircle />
        </div>
        <DefaultTooltip handle={`gen-logic-assignment`}>
          Select how to process assignments.
        </DefaultTooltip>
        <div className={styles["body__text"]}>Assignments</div>
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="radio"
          name={CFG_ASSIGNMENTS}
          value={VAL_ASSIGNMENTS_NORMAL}
          checked={assignment === VAL_ASSIGNMENTS_NORMAL}
          onChange={handleRadioChange}
        />
        {VAL_ASSIGNMENTS_NORMAL}
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="radio"
          name={CFG_ASSIGNMENTS}
          value={VAL_ASSIGNMENTS_IGNORE}
          checked={assignment === VAL_ASSIGNMENTS_IGNORE}
          onChange={handleRadioChange}
        />
        {VAL_ASSIGNMENTS_IGNORE}
      </div>
    </div>
  );
};

const GenBodyOptions = () => {
  const { data, handleChange } = useContext(GenerationContext);

  const _header = data[CFG_HEADER];
  const _separator = data[CFG_SEPARATOR];
  const _choiceNames = data[CFG_CHOICE_NAMES];

  return (
    <div className={styles["body__col-logic"]}>
      <div className={styles["body__row-logic"]}>
        <div
          className={styles["body__icon"]}
          data-tooltip-id={`gen-logic-assignment`}
          data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
          <IconInfoCircle />
        </div>
        <DefaultTooltip handle={`gen-logic-assignment`}>Additional options.</DefaultTooltip>
        <div className={styles["body__text"]}>Other</div>
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="checkbox"
          name={CFG_HEADER}
          checked={_header}
          onChange={() => handleChange({ target: { name: CFG_HEADER, value: !_header } })}
        />
        Include header
      </div>
      <div className={styles["body__row-small"]}>
        <input
          type="checkbox"
          name={CFG_CHOICE_NAMES}
          checked={_choiceNames}
          onChange={() =>
            handleChange({ target: { name: CFG_CHOICE_NAMES, value: !_choiceNames } })
          }
        />
        Use choice names instead of values
      </div>
      <div className={styles["body__row-small"]}>
        Separator
        <input type="text" name={CFG_SEPARATOR} value={_separator} onChange={handleChange} />
      </div>
    </div>
  );
};

const GenFooter = ({ children }) => {
  return <div className={styles["footer"]}>{children}</div>;
};

const GenFooterButtons = () => {
  const { data, name, modelYaml } = useContext(GenerationContext);
  const _endpointTomato = useSelector(getEndpointTomato);
  const _navigate = useNavigate();
  const _dispatch = useDispatch();

  let transmission = false;

  const _processChunkData = (chunk) => {
    const jsonStrings = chunk.trim().split("\n");
    const std = [];
    const err = [];

    if (jsonStrings) {
      const jsonObjects = jsonStrings.map((jsonStr) => JSON.parse(jsonStr));

      for (let i = 0; i < jsonObjects.length; i++) {
        const jsonObject = jsonObjects[i];
        if (jsonObject.stream === "begin") {
          transmission = true;
        } else if (jsonObject.stream === "end") {
          transmission = false;
        } else if (jsonObject.type === "std:out") {
          std.push(jsonObject.payload);
        } else if (jsonObject.type === "progress") {
          try {
            const progressData = JSON.parse(jsonObject.payload);
            _dispatch(
              setProgress({
                done: progressData.done,
                total: progressData.total,
                percentage: progressData.percentage,
              }),
            );
          } catch (error) {
            console.warn("Error parsing progress data:", jsonObject.payload);
          }
        } else if (jsonObject.type === "std:err" || jsonObject.type === "info") {
          err.push(jsonObject.payload);
        }
      }
    }

    if (std.length > 0) {
      _dispatch(addData(std));
    }

    if (err.length > 0) {
      if (err.length === 1 && err[0].startsWith("Reading model from stdin")) {
        return;
      }

      _dispatch(addErrors(err));
    }
  };

  const _processChunkError = (error) => {
    _dispatch(addErrors(error));
  };

  const _processChunkEnd = () => {
    _dispatch(setSpinner(false));
    if (transmission) {
      _dispatch(addErrors(["The transmission has been terminated"]));
    }
  };

  const _handleSubmit = (e) => {
    e?.preventDefault();

    _dispatch(resetData());

    _dispatch(setSpinner(true));
    _dispatch(setSpinnerMessage("Generating"));

    _navigate(Pages.TESTS, { state: { name } });

    requestGeneration(
      modelYaml,
      data,
      _processChunkData,
      _processChunkError,
      _processChunkEnd,
      _endpointTomato,
    );
  };

  return <ButtonGenerate text={"Generate"} handler={_handleSubmit} />;
};

export default DataGenerationPage;
